/* Native Shopify product checkout.
 * Refresh reference: buy-buttons.liquid and product-info.js.
 * Shopify owns wallet clicks, payment selection and checkout navigation.
 * This adapter only synchronizes AYEIOR's offer selector with one active form.
 */
(() => {
  if (!window.customElements || customElements.get('maison-accelerated-checkout')) return;

  customElements.define('maison-accelerated-checkout', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      queueMicrotask(() => {
        if (!this.isConnected || signal.aborted) return;
        this.root = this.closest('maison-product-page');
        this.mountPoint = this.querySelector('[data-checkout-slot]');
        this.primaryForm = this.root?.querySelector('form.maison-pdp__form');
        if (!this.root || !this.mountPoint) return;
        try {
          this.variants = JSON.parse(this.querySelector('[data-checkout-variants]')?.textContent || '[]');
          this.bundleVariants = JSON.parse(this.querySelector('[data-checkout-bundle-variants]')?.textContent || '[]');
          if (!Array.isArray(this.variants) || !Array.isArray(this.bundleVariants)) throw new Error('Invalid variants');
        } catch (_) {
          this.hidden = true;
          return;
        }

        this.root.addEventListener('change', event => {
          if (!(event.target instanceof Element) || this.contains(event.target)) return;
          if (event.target.matches('input[name="maison-variant"], input[name="maison-purchase-offer"], select[data-maison-bundle-variant], [data-maison-variant-id], [name="quantity"]')) {
            // Native input values must be ready before the next wallet click.
            this.sync();
          }
        }, { signal });
        this.root.addEventListener('input', event => {
          if (event.target instanceof Element && !this.contains(event.target) && event.target.matches('[name="quantity"]')) this.sync();
        }, { signal });
        window.addEventListener('pageshow', event => {
          this.sync(Boolean(event.persisted));
        }, { signal });
        window.addEventListener('load', () => this.initializePaymentButton(), { once: true, signal });
        this.sync(true);
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    initializePaymentButton() {
      if (!this.isConnected || this.hidden) return;
      // Same public initializer Refresh uses after inserting a new product form.
      // Never click/replay a wallet or manipulate its closed shadow DOM.
      if (typeof window.Shopify?.PaymentButton?.init === 'function') {
        window.Shopify.PaymentButton.init();
      }
    }

    sync(initialize = false) {
      if (!this.isConnected || !this.root || !this.mountPoint || !this.variants || this.syncing) return;
      this.syncing = true;
      try {
        const offer = this.root.querySelector('input[name="maison-purchase-offer"]:checked');
        const kind = offer?.dataset.offerKind === 'bundle' ? 'bundle' : 'single';
        const selects = [...(offer?.closest('.maison-pdp__offer')?.querySelectorAll('select[data-maison-bundle-variant]') || [])];
        let variant;
        if (kind === 'bundle') {
          const complete = selects.length === 3 && selects.every(select =>
            select.value && select.selectedOptions[0] && !select.selectedOptions[0].disabled
          );
          if (complete) {
            const titles = selects.map(select => select.selectedOptions[0].textContent.trim());
            variant = this.bundleVariants.length === 1 ? this.bundleVariants[0] : this.bundleVariants.find(candidate =>
              Array.isArray(candidate.options) && candidate.options.length === titles.length &&
              candidate.options.every((value, index) => value === titles[index])
            );
          }
        } else {
          const selectedId = selects.length
            ? selects[0].value
            : this.root.querySelector('input[name="maison-variant"]:checked')?.value ||
              this.primaryForm?.querySelector('[name="id"]')?.value;
          variant = this.variants.find(candidate => String(candidate.id) === String(selectedId));
          if (selects[0]?.selectedOptions[0]?.disabled) variant = null;
        }

        const min = Number(variant?.quantity_rule?.min) || 1;
        const step = Number(variant?.quantity_rule?.increment) || 1;
        const max = Number(variant?.quantity_rule?.max) || null;
        const quantitySource = kind === 'single' ? this.primaryForm?.elements.namedItem('quantity') : null;
        const quantity = quantitySource ? Number(quantitySource.value) : min;
        const available = variant?.available === true && Number.isInteger(quantity) &&
          quantity >= min && (quantity - min) % step === 0 && (max === null || quantity <= max);

        if (!available) {
          this.hidden = true;
          this.mountPoint.querySelectorAll('input[name="id"], input[name="quantity"]').forEach(input => { input.disabled = true; });
          return;
        }

        let wrapper = this.mountPoint.querySelector('[data-checkout-kind]');
        let replaced = false;
        if (wrapper?.dataset.checkoutKind !== kind) {
          const template = this.querySelector(`template[data-checkout-template="${kind}"]`);
          const next = template?.content.firstElementChild?.cloneNode(true);
          if (!next) { this.hidden = true; return; }
          // A second product's wallet is never mounted in a hidden form.
          this.mountPoint.replaceChildren(next);
          wrapper = next;
          replaced = true;
        }

        const form = wrapper.querySelector('form');
        const idInput = form?.querySelector('input[name="id"]');
        const quantityInput = form?.querySelector('input[name="quantity"]');
        if (!idInput || !quantityInput) { this.hidden = true; return; }
        const wasHidden = this.hidden;
        const idChanged = idInput.disabled || idInput.value !== String(variant.id);
        const quantityChanged = quantityInput.disabled || quantityInput.value !== String(quantity);
        // Set the complete native payload before notifying Shopify of changes.
        idInput.disabled = false;
        quantityInput.disabled = false;
        idInput.value = String(variant.id);
        quantityInput.value = String(quantity);
        this.hidden = false;
        if (idChanged || replaced) idInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (quantityChanged || replaced) quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (initialize || replaced || wasHidden) this.initializePaymentButton();
      } finally {
        this.syncing = false;
      }
    }
  });
})();
